import React, { useContext, useState, useEffect, Fragment } from "react";
import {
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Download,
  Home,
  User,
  Settings,
  LogOut,
  Clock,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { AuthContext } from "./context/AuthContext";
import TopbarNonav from "./components/TopbarNonav";
import axios from "axios";
import config from "./config/environment";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedDuplicate, setCheckedDuplicate] = useState(false);
  const { logout, userId, firstName } = useContext(AuthContext);

  // Define subscription plans from environment config (matches PaddlePaymentForm)
  const SUBSCRIPTION_PLANS = config.paddle.plans;

  useEffect(() => {
    // Check for duplicate subscription error in localStorage
    const checkForErrors = async () => {
      const checkoutError = localStorage.getItem("checkout_error");
      if (checkoutError && checkoutError.includes("already have")) {
        console.log("Duplicate subscription detected, redirecting...");
        navigate("/payment?error=duplicate");
        return;
      }
      setCheckedDuplicate(true);
    };

    checkForErrors();
  }, [navigate]);

  useEffect(() => {
    // Check for subscription in database
    const verifySubscriptionInDb = async () => {
      if (!userId) return;

      setLoading(true);

      try {
        // First check localStorage for checkout data
        const checkoutData = localStorage.getItem("paddleCheckoutData");

        if (checkoutData) {
          console.log("Found checkout data in localStorage, payment completed");
          // Always start with processing status for better UX
          setSubscriptionStatus("processing");
          setCheckedDuplicate(true);

          // Start checking for subscription confirmation after a short delay
          setTimeout(() => {
            checkSubscriptionStatus();
          }, 3000); // Wait 3 seconds before first check
        } else {
          console.warn("No checkout data found, redirecting to payment page");
          // If no checkout data, redirect back to payment page
          setTimeout(() => {
            navigate("/payment");
          }, 2000);
        }
      } catch (error) {
        console.error("Error in subscription verification:", error);
        setSubscriptionStatus("processing");
        setCheckedDuplicate(true);

        // Still try to check subscription status
        setTimeout(() => {
          checkSubscriptionStatus();
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    verifySubscriptionInDb();
  }, [userId, navigate]);

  useEffect(() => {
    // Process checkout data when duplicate check is complete
    if (!checkedDuplicate) return;

    try {
      const checkoutData = JSON.parse(
        localStorage.getItem("paddleCheckoutData")
      );
      const selectedPlan = localStorage.getItem("selectedPlan") || "monthly";

      if (!checkoutData) {
        throw new Error("No checkout data found");
      }

      // Extract data with fallbacks
      const planInfo = SUBSCRIPTION_PLANS[selectedPlan];
      const transactionId =
        checkoutData.data?.transaction_id ||
        (checkoutData.data?.subscription &&
          checkoutData.data.subscription.id) ||
        "N/A";
      const status = checkoutData.data?.status || "Pending verification";
      const amount =
        checkoutData.data.totals?.total || `$${planInfo.amount}.00`;
      const email =
        checkoutData.data?.customer?.email || localStorage.getItem("username");

      setPaymentDetails({
        transactionId,
        status,
        amount,
        interval: planInfo.interval,
        currency: checkoutData.data?.currency || "USD",
        plan: selectedPlan,
        email,
        date: new Date().toLocaleDateString(),
      });

      // Check subscription status from API
      if (userId) {
        checkSubscriptionStatus();
      }
    } catch (error) {
      console.error("Error loading payment details:", error);
      setPaymentDetails({
        error: true,
        message: "Failed to load payment details",
      });
      setLoading(false);
    }
  }, [checkedDuplicate, userId]);

  const checkSubscriptionStatus = async (attemptCount = 1) => {
    try {
      const response = await axios.get(
        `https://sqlguroo.com/api/subscriptions/check?userId=${userId}`
      );

      if (response.data.hasActiveSubscription) {
        setSubscriptionStatus(response.data.subscription.status);
        console.log(
          `Subscription confirmed on attempt ${attemptCount}:`,
          response.data.subscription.status
        );
      } else {
        console.log(
          `Subscription not found on attempt ${attemptCount}, will retry...`
        );

        // Retry up to 12 times (about 2 minutes total)
        if (attemptCount < 12) {
          setTimeout(() => {
            checkSubscriptionStatus(attemptCount + 1);
          }, 10000); // Wait 10 seconds between retries
        } else {
          // After 12 attempts, show not found
          console.log("Max retry attempts reached, subscription not found");
          setSubscriptionStatus("not_found");
          const checkoutError = localStorage.getItem("checkout_error");
          if (checkoutError) {
            setPaymentDetails({
              error: true,
              message: checkoutError,
            });
          }
        }
      }
    } catch (err) {
      console.error(
        `Error checking subscription status (attempt ${attemptCount}):`,
        err
      );

      // Retry on error too, but with fewer attempts
      if (attemptCount < 6) {
        setTimeout(() => {
          checkSubscriptionStatus(attemptCount + 1);
        }, 15000); // Wait 15 seconds between error retries
      } else {
        setSubscriptionStatus("error");
      }
    }
  };

  const refreshStatus = () => {
    setLoading(true);
    checkSubscriptionStatus();
  };

  // Handle user logout
  const handleLogout = async () => {
    localStorage.clear();
    await logout();
    navigate("/");
  };

  // Loading state
  if (loading) {
    return (
      <div className="relative flex min-h-screen justify-center bg-gray-50">
        <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto min-h-screen overflow-y-auto">
          <TopbarNonav />
          <div className="container mx-auto px-4 py-6 mb-20">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-lg w-full p-6 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h2 className="text-lg font-medium text-gray-800 mb-2">
                  Verifying your subscription
                </h2>
                <p className="text-sm text-gray-500">
                  This will just take a moment...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  if (!paymentDetails) {
    return (
      <div className="relative flex min-h-screen justify-center bg-gray-50">
        <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto min-h-screen overflow-y-auto">
          <TopbarNonav />
          <div className="container mx-auto px-4 py-6 mb-20">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-lg w-full p-6 text-center">
                <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-lg font-medium text-gray-800 mb-2">
                  Loading payment details
                </h2>
                <p className="text-sm text-gray-500">
                  Please wait while we retrieve your payment information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentDetails.error) {
    return (
      <div className="relative flex min-h-screen justify-center bg-gray-50">
        <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto min-h-screen overflow-y-auto">
          <TopbarNonav />
          <div className="container mx-auto px-4 py-6 mb-20">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-lg w-full p-6 text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-lg font-medium text-gray-800 mb-2">
                  Payment Error
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  {paymentDetails.message ||
                    "An error occurred with your payment"}
                </p>
                <button
                  onClick={() => navigate("/payment")}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm text-sm"
                >
                  Return to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment processing state
  if (subscriptionStatus === "processing") {
    return (
      <div className="relative flex min-h-screen justify-center bg-gray-50">
        <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto min-h-screen overflow-y-auto">
          <TopbarNonav />
          <div className="container mx-auto px-4 py-6 mb-20">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-lg w-full p-6 space-y-4">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                    <Clock className="w-8 h-8 text-blue-600 animate-pulse" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Processing Your Payment
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Thank you for your purchase! We're confirming your payment
                    and setting up your subscription.
                  </p>
                  <div className="bg-blue-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-center mb-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-blue-800 font-medium text-sm">
                        Confirming payment...
                      </span>
                    </div>
                    <p className="text-xs text-blue-700">
                      This usually takes 30-60 seconds. Please don't close this
                      page.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <button
                      onClick={() => checkSubscriptionStatus(1)}
                      className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 rounded-lg text-white hover:bg-blue-700 transition-colors shadow-sm text-sm"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Check Status Now
                    </button>
                    <p className="text-xs text-gray-500">
                      Having trouble? Contact our support team for assistance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No subscription found
  if (subscriptionStatus === "not_found") {
    return (
      <div className="relative flex min-h-screen justify-center bg-gray-50">
        <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto min-h-screen overflow-y-auto">
          <TopbarNonav />
          <div className="container mx-auto px-4 py-6 mb-20">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-lg w-full p-6 space-y-4">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Subscription Not Found
                  </h2>
                  <p className="text-sm text-gray-600 mb-3">
                    We couldn't find an active subscription for your account.
                    This could happen if:
                  </p>
                  <ul className="text-left text-sm text-gray-600 pl-4 list-disc space-y-1 mb-4">
                    <li>Your payment was declined by your bank</li>
                    <li>You already have an active subscription</li>
                    <li>There was an error processing your payment</li>
                  </ul>
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate("/")}
                      className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 rounded-lg text-white hover:bg-blue-700 transition-colors shadow-sm text-sm"
                    >
                      <Home className="w-4 h-4" />
                      Return to Home
                    </button>
                    <button
                      onClick={() => navigate("/pricing")}
                      className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      View Pricing Plans
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main success view
  return (
    <div className="relative flex min-h-screen justify-center bg-gray-50">
      <div className="relative flex flex-col w-full max-w-[98%] 2xl:max-w-[1820px] mx-auto min-h-screen overflow-y-auto">
        <TopbarNonav />
        <div className="container mx-auto px-4 py-6 mb-20">
          <main className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Success Banner */}
              <div
                className={`p-6 text-center ${
                  subscriptionStatus === "pending"
                    ? "bg-yellow-50"
                    : "bg-green-50"
                }`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white mb-4">
                  {subscriptionStatus === "pending" ? (
                    <Clock className="w-8 h-8 text-yellow-500" />
                  ) : (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  )}
                </div>

                {subscriptionStatus === "pending" ? (
                  <>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">
                      Payment Processing
                    </h1>
                    <p className="text-sm text-gray-700 max-w-md mx-auto mb-3">
                      Thank you for subscribing to SQL Guroo! Your payment is
                      being processed and your account will be activated
                      shortly.
                    </p>
                    <div className="mb-4 p-3 bg-white rounded-lg border border-yellow-200 text-xs text-yellow-700 max-w-md mx-auto">
                      This usually takes just a few minutes. If your
                      subscription isn't active after 15 minutes, please contact
                      support.
                    </div>
                    <button
                      onClick={refreshStatus}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition shadow-sm text-sm"
                    >
                      Check Status
                    </button>
                  </>
                ) : (
                  <>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">
                      Payment Successful!
                    </h1>
                    <p className="text-sm text-gray-700 max-w-md mx-auto">
                      Thank you for subscribing to SQL Guroo! Your account has
                      been successfully activated with full access to all
                      premium features.
                    </p>
                  </>
                )}
              </div>

              {/* Payment Details */}
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Payment Details
                </h2>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-100">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Amount Paid</span>
                    <span className="font-semibold text-gray-900 text-sm">
                      {paymentDetails.amount}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Plan</span>
                    <span className="font-semibold text-gray-900 capitalize text-sm">
                      {paymentDetails.plan} (
                      {paymentDetails.plan === "monthly" ? "Monthly" : "Annual"}{" "}
                      Billing)
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">
                      Transaction ID
                    </span>
                    <span className="text-gray-900 font-mono text-xs">
                      {paymentDetails.transactionId}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Email</span>
                    <span className="text-gray-900 text-sm">
                      {paymentDetails.email}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <span className="text-sm text-gray-600">Date</span>
                    <span className="text-gray-900 text-sm">
                      {paymentDetails.date}
                    </span>
                  </div>
                </div>

                <div className="mt-6">
                  {/* Navigation Buttons */}
                  <div className="flex gap-3">
                    <Link
                      to="/"
                      className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 rounded-lg text-white hover:bg-blue-700 transition-colors shadow-sm text-sm flex-1"
                    >
                      <Home className="w-4 h-4" />
                      Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
